import { AllocationRule } from 'further-types/lp/allocations';
import { round, sumBy } from 'lodash';
import { z } from 'zod';
import { CalendarDay } from '../../../utils';
import { isMongoId } from '../../helpers';
import { allocationMatchesCheckAmount } from '../../helpers/transaction-allocations-input';
import { CreateAllocation } from '../allocations';

const ManualActionSchema = z.object({
  ledgerDate: z.instanceof(CalendarDay, {
    message: 'Please select a ledger date.',
  }),
  transactions: z
    .array(
      z
        .object({
          id: z.string().optional(),
          reconcilableTransactionId: z
            .string()
            .refine(isMongoId({ optional: true }), {
              message: 'Please select a valid transaction to reconcile.',
            })
            .optional(),
          transactionTypeId: z.string().refine(isMongoId(), {
            message: 'Please select a transaction type.',
          }),
          debitAmountLcy: z.number().min(0),
          debitAmountTcy: z.number().min(0),
          creditAmountLcy: z.number().min(0),
          creditAmountTcy: z.number().min(0),
        })
        .refine(
          (data) =>
            (data.debitAmountLcy && data.debitAmountTcy) ||
            (data.creditAmountLcy && data.creditAmountTcy),
          { message: 'You have to enter at least one amount.' },
        ),
    )
    .min(2, {
      message: 'Please enter at least two transactions.',
    }),
  description: z.string().min(1, {
    message: 'Please enter a description.',
  }),
  allocationRule: z.nativeEnum(AllocationRule, {
    message: 'Please select an allocation rule.',
  }),
  allocations: z.array(CreateAllocation, {
    message: 'Please allocate the value.',
  }),
  totalAmountLcy: z.number().min(0.01, {
    message: 'Please enter a total amount.',
  }),
  totalAmountTcy: z.number().min(0.01, {
    message: 'Please enter a total amount.',
  }),
});

export const CreateManualAction = ManualActionSchema.refine(
  (data) => allocationMatchesCheckAmount(data.totalAmountTcy, data.allocations),
  {
    message: 'Total allocation amount must match input amount.',
    path: ['allocations'],
  },
).refine(
  (data) =>
    round(
      sumBy(data.transactions, (transaction) => transaction.debitAmountLcy),
      2,
    ) ===
    round(
      sumBy(data.transactions, (transaction) => transaction.creditAmountLcy),
      2,
    ),
  {
    message: 'Credit and debit amounts must match.',
    path: ['transactions'],
  },
);

export const UpdateManualAction = ManualActionSchema.extend({
  id: z.string().refine(isMongoId()),
})
  .refine(
    (data) =>
      allocationMatchesCheckAmount(data.totalAmountTcy, data.allocations),
    {
      message: 'Total allocation amount must match input amount.',
      path: ['allocations'],
    },
  )
  .refine(
    (data) =>
      round(
        sumBy(data.transactions, (transaction) => transaction.debitAmountLcy),
        2,
      ) ===
      round(
        sumBy(data.transactions, (transaction) => transaction.creditAmountLcy),
        2,
      ),
    {
      message: 'Credit and debit amounts must match.',
      path: ['transactions'],
    },
  );
